import { isAxiosError } from "axios"
import { useCallback, useState } from "react"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_SMOOTHER_API_URL } from "src/lib/constants"
import useSWR from "swr"
import { toRequestBody } from "../lib/formValues"
import type {
  BankTransferResult,
  BankTransferResultFormValues,
  BankTransferResultListItem,
  Pagination,
} from "../types"

const ENDPOINT = `${SMOOTH_SMOOTHER_API_URL}/screen/bank_transfer_results`

export const useBankTransferResults = (page: number) => {
  const { getWithToken } = useSmoothApi()
  const { data, error, isLoading } = useSWR(
    `${ENDPOINT}?page=${page}`,
    async (url: string) =>
      (await getWithToken(url)).data as {
        bankTransferResults: BankTransferResultListItem[]
        pagination: Pagination
      },
    { revalidateOnFocus: false }
  )

  return {
    bankTransferResults: data?.bankTransferResults ?? [],
    pagination: data?.pagination ?? null,
    error: error ?? null,
    isLoading,
  }
}

export const useBankTransferResult = (id: number | null) => {
  const { getWithToken } = useSmoothApi()
  const { data, error, isLoading, mutate } = useSWR(
    id ? `${ENDPOINT}/${id}` : null,
    async (url: string) => (await getWithToken(url)).data as { bankTransferResult: BankTransferResult },
    { revalidateOnFocus: false }
  )

  return { bankTransferResult: data?.bankTransferResult ?? null, error: error ?? null, isLoading, mutate }
}

type SaveResult = { ok: true } | { ok: false; message: string }

const toMessage = (e: unknown, fallback: string) =>
  isAxiosError(e) && typeof e.response?.data?.message === "string" ? e.response.data.message : fallback

/** 振込結果の更新・削除。失敗したときは API のメッセージを返す */
export const useSaveBankTransferResult = () => {
  const { patchWithToken, deleteWithToken } = useSmoothApi()
  const [isSaving, setIsSaving] = useState(false)

  const run = useCallback(async (request: () => Promise<unknown>, fallback: string): Promise<SaveResult> => {
    setIsSaving(true)
    try {
      await request()
      return { ok: true }
    } catch (e) {
      return { ok: false, message: toMessage(e, fallback) }
    } finally {
      setIsSaving(false)
    }
  }, [])

  const update = useCallback(
    (id: number, values: BankTransferResultFormValues) =>
      run(() => patchWithToken(`${ENDPOINT}/${id}`, toRequestBody(values)), "保存に失敗しました"),
    [patchWithToken, run]
  )

  const destroy = useCallback(
    (id: number) => run(() => deleteWithToken(`${ENDPOINT}/${id}`), "削除に失敗しました"),
    [deleteWithToken, run]
  )

  return { update, destroy, isSaving }
}
